import { useEffect, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  FileText,
  Bell,
  Settings,
  Users,
  Ship,
  BarChart2,
  Shield,
  Server,
  User as UserIcon,
  Grid3x3,
  ChevronLeft,
  ChevronRight,
  LogOut,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { useAuthStore } from '@/store/authStore';
import type { UserRole } from '@/store/authStore';

interface NavItem {
  label: string;
  path: string;
  icon: React.ElementType;
  roles: UserRole[];
  comingSoon?: boolean;
}

const ALL_ROLES: UserRole[] = [
  'platform_admin',
  'tenant_admin',
  'safety_manager',
  'supervisor',
  'executive',
  'ssr',
];

const NAV_ITEMS: NavItem[] = [
  // Home (Platform Control Panel) — visible to every web role.
  { label: 'Home', path: '/home', icon: Grid3x3, roles: ALL_ROLES },

  // SSR sees a focused field-first set: Today, My Project Reports, Alerts, Profile.
  // Management nav items (Workforce, Analytics, Admin, Settings, Platform Admin) are
  // intentionally hidden from SSRs.
  { label: 'Today', path: '/dashboard', icon: LayoutDashboard, roles: ['ssr'] },
  { label: 'My Project Reports', path: '/reports', icon: FileText, roles: ['ssr'] },
  { label: 'Alerts', path: '/alerts', icon: Bell, roles: ['ssr'] },
  { label: 'POB', path: '/pob', icon: Ship, roles: ['ssr'] },
  { label: 'Profile', path: '/profile', icon: UserIcon, roles: ['ssr'] },

  { label: 'Dashboard', path: '/dashboard', icon: LayoutDashboard, roles: ['supervisor', 'safety_manager', 'tenant_admin', 'executive', 'platform_admin'] },
  { label: 'DSR Reports', path: '/reports', icon: FileText, roles: ['supervisor', 'safety_manager', 'tenant_admin', 'executive', 'platform_admin'] },
  { label: 'POB', path: '/pob', icon: Ship, roles: ['supervisor', 'safety_manager', 'tenant_admin', 'executive', 'platform_admin'] },
  { label: 'Workforce', path: '/workforce', icon: Users, roles: ['safety_manager', 'tenant_admin', 'platform_admin'] },
  { label: 'Alerts', path: '/alerts', icon: Bell, roles: ['supervisor', 'safety_manager', 'tenant_admin', 'platform_admin'] },
  { label: 'Analytics', path: '/analytics', icon: BarChart2, roles: ['safety_manager', 'tenant_admin', 'executive', 'platform_admin'], comingSoon: true },
  { label: 'Settings', path: '/settings', icon: Settings, roles: ['supervisor', 'safety_manager', 'tenant_admin', 'executive', 'platform_admin'] },
  { label: 'Admin', path: '/admin', icon: Shield, roles: ['tenant_admin', 'platform_admin'] },
  { label: 'Platform Admin', path: '/platform-admin', icon: Server, roles: ['platform_admin'] },
];

const STORAGE_KEY = 'mozaic-sidebar-collapsed';

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onMobileClose?: () => void;
  isMobileSheet?: boolean;
}

export default function Sidebar({ collapsed, onToggle, onMobileClose, isMobileSheet }: SidebarProps) {
  const { userRole, user, signOut } = useAuthStore();
  const navigate = useNavigate();
  const [autoCollapsed, setAutoCollapsed] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth <= 1024 && window.innerWidth > 768) {
        setAutoCollapsed(true);
      } else {
        setAutoCollapsed(false);
      }
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const isCollapsed = isMobileSheet ? false : (collapsed || autoCollapsed);
  const visibleItems = NAV_ITEMS.filter((item) => userRole && item.roles.includes(userRole));

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  const handleNavClick = () => {
    onMobileClose?.();
  };

  return (
    <div
      className={`flex h-full flex-col bg-mozaic-navy text-white transition-[width] duration-200 ${
        isCollapsed ? 'w-16' : 'w-60'
      }`}
    >
      {/* Logo */}
      <div className="flex h-14 items-center gap-2 px-4">
        <img
          src="/mozaic-logo.png"
          alt="MOZAIC"
          className="h-8 w-8"
        />
        {!isCollapsed && (
          <span className="text-xl font-bold tracking-tight text-mozaic-teal">
            MOZAIC
          </span>
        )}
      </div>

      {/* Nav */}
      <nav className="flex-1 space-y-1 overflow-y-auto px-2 py-2">
        {visibleItems.map((item) => {
          const Icon = item.icon;

          if (item.comingSoon) {
            return (
              <div
                key={item.path}
                className="flex items-center gap-3 rounded-md px-3 py-2 text-sm text-white/40 cursor-not-allowed"
                title={`${item.label} — Coming Soon`}
              >
                <Icon className="h-5 w-5 shrink-0" />
                {!isCollapsed && (
                  <>
                    <span>{item.label}</span>
                    <Badge variant="outline" className="ml-auto border-white/20 text-[10px] text-white/40">
                      Soon
                    </Badge>
                  </>
                )}
              </div>
            );
          }

          return (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={handleNavClick}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'border-l-4 border-mozaic-teal bg-white/10 text-white pl-2'
                    : 'text-white/60 hover:bg-white/5 hover:text-white'
                }`
              }
            >
              <Icon className="h-5 w-5 shrink-0" />
              {!isCollapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>

      {/* User + sign out */}
      <div className="border-t border-white/10 p-3">
        {!isCollapsed && (
          <div className="mb-2 truncate text-xs text-white/60">
            {user?.email}
          </div>
        )}
        {!isCollapsed && userRole && (
          <Badge className="mb-2 bg-mozaic-teal/20 text-mozaic-teal text-xs capitalize">
            {userRole.replace('_', ' ')}
          </Badge>
        )}
        <button
          onClick={handleSignOut}
          className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-sm text-white/60 hover:bg-white/5 hover:text-white"
        >
          <LogOut className="h-4 w-4 shrink-0" />
          {!isCollapsed && <span>Sign Out</span>}
        </button>
      </div>

      {/* Collapse toggle — hidden in mobile sheet */}
      {!isMobileSheet && (
        <button
          onClick={onToggle}
          className="absolute right-0 top-1/2 z-10 -translate-y-1/2 translate-x-1/2 rounded-full border border-white/20 bg-mozaic-navy p-1 text-white/60 hover:text-white"
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {isCollapsed ? <ChevronRight className="h-3 w-3" /> : <ChevronLeft className="h-3 w-3" />}
        </button>
      )}
    </div>
  );
}

export function useSidebarCollapsed() {
  const [collapsed, setCollapsed] = useState(() => {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  });

  const toggle = () => {
    setCollapsed((prev) => {
      const next = !prev;
      localStorage.setItem(STORAGE_KEY, String(next));
      return next;
    });
  };

  return { collapsed, toggle };
}
